import { conversationUIStateAtom } from "@plura/client/hooks";
import { FormElementType, FormJourneyItem, LeadForm } from "@plura/shared/types";
import { motion } from "framer-motion";
import { useAtom } from "jotai";

import { cn } from "@plura/shared/utils";
import { Icon } from "@plura/client/components";
import './conversation-pagination.css';

interface Props {
  leadForm: LeadForm
  currentStep: FormJourneyItem
  previousJourneyItem: FormJourneyItem | null
  goBack?: () => void,
  handleAnswer?: () => void,
  allowedToGoToNextJourneyItem: boolean
  hideNextButton?: boolean
  theme: FormElementType
  previewMode?: boolean
}

const ConversationPagination = ({
  leadForm,
  currentStep,
  goBack,
  handleAnswer,
  previousJourneyItem,
  allowedToGoToNextJourneyItem,
  hideNextButton,
  theme,
  previewMode
}: Props) => {
  const [{ hideUI }] = useAtom(conversationUIStateAtom)
  const isLastStep = currentStep.order === leadForm.journeyItems?.length && leadForm.journeyItems?.length - 1

  if (isLastStep) return null

  const themeKey = theme.toLowerCase() as 'rounded' | 'squared' | 'semi_rounded';
  const isRounded = themeKey === 'rounded';

  return (
    <motion.div
      className={cn(
        'plura-conversation-pagination absolute bottom-5 right-5 z-10 flex h-10 flex-row justify-end',
      )}
      exit={{ opacity: 0, transform: 'translateY(-30px)' }}
      initial={{ opacity: 0, transform: 'translateY(30px)' }}
      animate={{ opacity: 1, transform: 'translateY(0)' }}
      transition={{ duration: 0.5, ease: 'easeInOut' }}
    >
      <button
        type="button"
        onClick={goBack}
        className={cn(
          'flex h-10 w-10 items-center justify-center border border-solid text-base transition-all duration-200 ease-in-out',
          'border-[color:var(--button-color)] bg-[color:var(--button-color)] text-[color:var(--button-text-color)]',
          hideNextButton
            ? 'rounded-[var(--border-radius)]'
            : 'rounded-tl-[var(--border-radius)] rounded-bl-[var(--border-radius)]',
          'hover:bg-[color:var(--button-text-color)] hover:text-[color:var(--button-color)]',
          (!previousJourneyItem || hideUI || !goBack) && 'pointer-events-none opacity-50',
        )}
      >
        <Icon
          name="chevron"
          className={cn(
            '!size-8 fill-current',
            /* Chevron asset points down; always show previous/next as left/right */
            'rotate-90',
            isRounded && 'translate-x-[3px]',
          )}
        />
      </button>
      {!hideNextButton && (
      <button
        type="button"
        className={cn(
          'flex h-10 w-10 items-center justify-center border border-solid text-base transition-all duration-200 ease-in-out',
          'border-[color:var(--button-color)] bg-[color:var(--button-color)] text-[color:var(--button-text-color)]',
          'rounded-tr-[var(--border-radius)] rounded-br-[var(--border-radius)]',
          'hover:bg-[color:var(--button-text-color)] hover:text-[color:var(--button-color)]',
          (hideUI || !handleAnswer || !allowedToGoToNextJourneyItem) && 'pointer-events-none opacity-50',
        )}
        onClick={handleAnswer}
      >
        <Icon
          name="chevron"
          className={cn(
            '!size-8 fill-current',
            '-rotate-90',
            isRounded && '-translate-x-[3px]',
          )}
        />
      </button>
      )}
    </motion.div>
  )
}

export default ConversationPagination
